import type { MaybeRef } from '@vueuse/core'
import type { User as FirebaseUser } from 'firebase/auth'
import { useStorage } from '@vueuse/core'
import { createUser, getUserById, getUserFollowees } from '../api/users'
import type { User, UserMinified } from '../types/user'
import type { StripeAccountResponse } from '../types/api'
import { getStripeAccountState } from '../api/stripe'

export const useUser = () => useState<null | User>('user', () => null)
export const useUserStripeAccount = () => useState<null | StripeAccountResponse>('user-stripe-account', () => null)

export async function useAuthenticated() {
  const { firebaseUser } = useFirebaseAuth()

  await until(firebaseUser).toMatch(v => !!v, {
    timeout: 1000,
  })

  return !!firebaseUser.value
}

export async function createOrSigninUser(firebaseUser: MaybeRef<FirebaseUser>) {
  const { displayName, email, photoURL, uid } = unref(firebaseUser)

  const urlParams = new URLSearchParams(window.location.search)

  if (urlParams.get('src') === 'Magic')
    sendUserAuthToMagic(unref(firebaseUser))

  const user = useUser()
  const isLoggedIn = useStorage('is_logged_in', false)

  try {
    const foundUser = await getUserById(uid)

    if (foundUser?.id) {
      const userStripeAccount = useUserStripeAccount()
      user.value = foundUser
      isLoggedIn.value = true
      userStripeAccount.value = await getStripeAccountState()
    }
  }
  catch (e) {
    const createdUser = await createUser(uid, {
      name: displayName || '',
      email: email || '',
      avatar: photoURL || '',
    })

    if (!createdUser)
      return

    sendGTMEvent({ event: 'user-signup' })
    user.value = createdUser
    isLoggedIn.value = true
  }
}

export const useFollowingUsers = () => useState<User[]>('user:followed', () => [])
export const useLoadingFollowingUsers = () => useState('user:followed-loading', () => false)
export async function loadFollowingUsers() {
  const user = useUser()

  if (!user.value)
    return

  const followedUsers = useFollowingUsers()
  const isLoading = useLoadingFollowingUsers()

  isLoading.value = true
  followedUsers.value = await getUserFollowees(user.value.id) || []
  isLoading.value = false
}

export function formatUserName(user?: MaybeRef<User | UserMinified>) {
  const storedUser = useUser()

  if (!user && !storedUser.value)
    return ''

  const { name, username } = unref(user) || storedUser.value || {}

  if (!name && !username)
    return ''

  return limitString(name || username || '', {
    maxWords: 3,
    maxCharacters: 20,
  })
}
